import { useState } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import type { SelectOrWriteOption } from '@/components/inputs/select-or-write'
import { COURSE_STATUS } from '@/constants/course-status'
import type {
  MatchCoursesFormType,
  PreviousCoursesFormType
} from '@/schemas/education/match-courses-schema'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import type { PreviousEducationCourse } from '@/types/education/previous-education-course'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import type { SelectWithAddOption } from '@/types/select/select-with-add-option'

export const useCoursesEquivalencesRow = (
  rowIndex: number,
  availableCollegeCourses?: DesiredEducationCourse[],
  availablePreviousCourses?: PreviousEducationCourse[],
  formMethods?: StudentRequestedCoursesFormMethods,
  fieldKey:
    | 'courseEquivalences'
    | 'automaticCourseEquivalences' = 'courseEquivalences'
) => {
  const [backupValue, setBackupValue] = useState<Map<number, any>>(new Map())

  const fieldPath = (index: number) => `${fieldKey}.${index}` as const

  const insertBackupValue = (index: number, value: any) => {
    setBackupValue((prev: any) => {
      const copy = new Map<number, any>(prev)
      copy.set(index, value)
      return copy
    })
  }

  const removeBackupValue = (index: number) => {
    setBackupValue((prev: any) => {
      const copy = new Map<number, any>(prev)
      copy.delete(index)
      return copy
    })
  }

  const canUsePreviousCourse = (previousCourse?: PreviousCoursesFormType) => {
    if (!previousCourse) return true
    const currentFields = formMethods?.getValues?.().courseEquivalences || []
    return !currentFields.find((field) => {
      return field?.previousCourse?.id === previousCourse.id
    })
  }

  const getBackupValue = (index: number) => {
    const backup = backupValue.get(index)
    const canUse = backup && canUsePreviousCourse(backup.previousCourse)
    return backup && canUse ? backup : undefined
  }

  const fieldHasValue = (index: number) => {
    const currentFields = formMethods?.getValues?.()?.[fieldKey] || []
    return currentFields[index]?.previousCourse?.id !== undefined
  }

  const getCollegeCourseOptions = () => {
    const options: SelectOrWriteOption[] =
      availableCollegeCourses?.map((course) => {
        return {
          id: course.courseId,
          label: course.name,
          data: { ...course, serie: String(course.serie) }
        } as SelectOrWriteOption
      }) || []

    return options
  }

  const getPreviousCoursesOptions = () => {
    const options: SelectOrWriteOption[] =
      availablePreviousCourses?.map(
        (course) =>
          ({
            id: course.id,
            label: course.name,
            data: {
              ...course,
              finalNumericGrade: String(course.finalNumericGrade)
            }
          }) as SelectOrWriteOption
      ) || []

    return options
  }

  const handleRemoveField = () => {
    if (fieldKey !== 'courseEquivalences') return

    formMethods?.remove?.(rowIndex)
    removeBackupValue(rowIndex)
  }

  const handleDesiredCourseSelect = (
    value: SelectWithAddOption,
    index: number
  ) => {
    formMethods?.setValue?.(
      `${fieldPath(index)}.desiredCourse` as any,
      value.data,
      {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true
      }
    )
  }

  const handlePreviousCourseValue = (
    value: SelectWithAddOption,
    index: number
  ) => {
    if (value.data) {
      formMethods?.setValue?.(
        `${fieldPath(index)}.previousCourse` as any,
        value.data,
        {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true
        }
      )
    } else {
      formMethods?.setValue?.(
        `${fieldPath(index)}.previousCourse.id` as any,
        '',
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        }
      )

      formMethods?.setValue?.(
        `${fieldPath(index)}.previousCourse.name` as any,
        value.label,
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        }
      )
    }
  }

  const handleWorkload = (value: string, index: number) => {
    formMethods?.setValue?.(
      `${fieldPath(index)}.previousCourse.workload` as any,
      Number(value.trim()),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      }
    )
  }

  const handleFinalNumericGrade = (value: string, index: number) => {
    formMethods?.setValue?.(
      `${fieldPath(index)}.previousCourse.finalNumericGrade` as any,
      String(value).trim(),
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      }
    )
  }

  const handlePeriod = (value: string, index: number) => {
    formMethods?.setValue?.(
      `${fieldPath(index)}.previousCourse.period` as any,
      value,
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      }
    )
  }

  const handleStatusChange = (
    value: string,
    field: ControllerRenderProps<
      MatchCoursesFormType,
      `courseEquivalences.${number}` | `automaticCourseEquivalences.${number}`
    >,
    index: number
  ) => {
    if (value === COURSE_STATUS.ADAPTATION.id) {
      const value = structuredClone(field.value)
      insertBackupValue(index, value)

      formMethods?.setValue?.(
        `${fieldPath(index)}.previousCourse` as any,
        undefined,
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      )

      formMethods?.setValue?.(
        `${fieldPath(index)}.equivalenceStatus` as any,
        { id: COURSE_STATUS.ADAPTATION.id },
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      )

      return
    }

    const backup = getBackupValue(index)

    if (backup && canUsePreviousCourse(backup.previousCourse)) {
      formMethods?.setValue?.(
        `${fieldPath(index)}.previousCourse` as any,
        backup.previousCourse,
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      )
    }

    formMethods?.setValue?.(
      `${fieldPath(index)}.equivalenceStatus` as any,
      {
        id: value
      },
      { shouldDirty: true, shouldTouch: true, shouldValidate: true }
    )
  }

  return {
    states: {},
    actions: {
      handleDesiredCourseSelect,
      getCollegeCourseOptions,
      getPreviousCoursesOptions,
      handlePreviousCourseValue,
      handleWorkload,
      handleFinalNumericGrade,
      handlePeriod,
      handleStatusChange,
      getBackupValue,
      fieldHasValue,
      handleRemoveField
    }
  }
}
